// Central site content for Adeshi Moffi executive presence.
// Kept as data so pages remain thin & consistent.

export const CANONICAL_ORIGIN = "https://moffim.com";
export const LEADERSHIP_PATH = "/leadership";

export const executive = {
  name: "Adeshi Moffi",
  firstName: "Adeshi",
  lastName: "Moffi",
  title: "President & Managing Partner",
  shortTitle: "President, Investors Pathway LLC",
  company: "Investors Pathway LLC",
  companyShort: "Investors Pathway",
  companyUrl: "https://investorspathway.com",
  contactEmail: "ad.moffi@investorspathway.com",
  location: "Allen, Texas · Dallas–Fort Worth",
  address: {
    streetAddress: "550 S Watters Rd",
    addressLocality: "Allen",
    addressRegion: "TX",
    postalCode: "75013",
    addressCountry: "US",
  },
  summary:
    "Business Development, Strategic Advisory & Capital Readiness.",
  headshot:
    "https://customer-assets-jai6qajn.emergentagent.net/job_pe-leader/artifacts/62ugxcdu_245f3ebb-91c4-4260-b2f4-a5c3c593b6fb.jpeg",
  headshotAlt:
    "Portrait of Adeshi Moffi, President & Managing Partner of Investors Pathway LLC",
  bio: [
    "Adeshi Moffi serves as President of Investors Pathway LLC, where he leads the firm's strategic direction across business development, capital advisory, strategic partnerships, market expansion, and long-term growth initiatives.",
    "Drawing on experience spanning corporate leadership, consulting, entrepreneurship, and growth strategy, Moffi has built a reputation for identifying scalable opportunities, structuring strategic partnerships, and helping organizations accelerate growth through disciplined execution. His professional background includes experience with JPMorgan Chase & Co., BKD, and other leading organizations, where he contributed to enterprise initiatives, operational strategy, risk management, and projects within Fortune 100 environments.",
    "Throughout his career, Moffi has worked across multiple industries, including technology, consumer products, financial services, energy, and emerging growth businesses. He has supported strategic initiatives and transactions exceeding $250 million while leading ventures focused on business transformation, capital formation, and market expansion. He also founded and led the development of a technology venture that achieved a pre-money valuation exceeding $12 million, demonstrating experience in scaling businesses from concept through commercialization and investor readiness.",
    "Under his leadership, Investors Pathway has evolved into a strategic growth advisory platform that connects businesses with capital sources, institutional relationships, strategic partnerships, distribution opportunities, and expansion initiatives. The firm works with founders, executives, investors, and organizations seeking to accelerate growth, strengthen operational capabilities, enter new markets, and build enduring enterprise value.",
    "Moffi's leadership philosophy is grounded in disciplined execution, strategic thinking, and long-term value creation. He is recognized for combining analytical decision-making with entrepreneurial vision to help organizations navigate complex growth opportunities while building sustainable competitive advantages.",
    "Beyond his executive leadership, Moffi has participated in commercial modeling campaigns with nationally recognized brands, reflecting his belief that leadership, personal brand, and market presence are integral components of modern business development.",
    "Today, Moffi continues to cultivate strategic relationships across the private sector, investment community, and global business ecosystem, with a focus on creating lasting partnerships, unlocking growth opportunities, and delivering measurable outcomes for businesses, investors, and stakeholders across domestic and international markets.",
  ],
  priorFirms: [
    { name: "Investors Pathway LLC", role: "President & Managing Partner", period: "Present" },
    { name: "JPMorgan Chase & Co.", role: "Enterprise Initiatives", period: "Prior" },
    { name: "BKD", role: "Advisory & Operational Strategy", period: "Prior" },
  ],
  expertise: [
    "Private Equity",
    "Strategic Advisory",
    "Business Development",
    "Strategic Partnerships",
    "Consumer Brands",
    "Retail Expansion",
    "Manufacturing Growth",
    "Investment Strategy",
    "Business Acquisitions",
    "Capital Raising",
    "Commercial Real Estate",
    "Real Estate Funds",
  ],
  speakingTopics: [
    "The Discipline of Capital: How Institutional Capital is Deployed in the Middle Market",
    "Consumer Brand Investing in a Post-DTC Era",
    "Retail Expansion & Distribution Strategy for Growth-Stage Brands",
    "Manufacturing Growth, Reshoring, and Industrial Capital Formation",
    "Family Office Investing: Long-Duration Capital for Founder-Led Businesses",
    "From Concept to Investor Readiness: Building Enterprise Value",
  ],
  achievements: [
    { k: "$500M+", v: "Capital formation supported through the Investors Pathway strategic partnership network." },
    { k: "200+", v: "Transactions closed through the firm's network of strategic partnerships across private equity, consumer, retail, and manufacturing." },
    { k: "$12M+", v: "Pre-money valuation achieved on a founder-led technology venture." },
    { k: "10+", v: "Years of professional experience across corporate leadership, consulting, entrepreneurship, and growth strategy." },
    { k: "Fortune 100", v: "Enterprise initiatives contributed to during prior corporate tenures." },
  ],
  socials: [
    { label: "Instagram", url: "https://www.instagram.com/adeshimoffi",                          handle: "@adeshimoffi" },
    { label: "YouTube",   url: "https://youtube.com/@adeshimoffi?si=yIEpBmZXFEScBeox",           handle: "@adeshimoffi" },
  ],
};

export const philosophy = {
  headline:
    "Disciplined execution, strategic thinking, and long-term value creation.",
  body:
    "Leadership at Investors Pathway is grounded in analytical decision-making paired with entrepreneurial vision — helping organizations navigate complex growth opportunities while building sustainable competitive advantages.",
};

export const articles = [
  {
    slug: "private-equity-outlook",
    category: "Private Equity",
    title:
      "The Discipline of Capital: A Practitioner's Outlook on Private Equity in the Middle Market",
    dek:
      "Why deal discipline, not deal volume, will separate franchise firms from tourists over the next cycle.",
    date: "2026-01-14",
    readTime: "8 min read",
    image:
      "https://images.pexels.com/photos/6949931/pexels-photo-6949931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "Private equity has never suffered from a shortage of capital. It has always suffered from a shortage of discipline. As dry powder continues to compound and competition for high-quality assets intensifies, the firms that will build durable franchises are those that treat capital as a scarce resource — even when balance sheets suggest otherwise." },
      { type: "h2", text: "Selectivity as a Strategy" },
      { type: "p", text: "In the middle market, the difference between a top-quartile outcome and a mediocre one is rarely macro. It is almost always the underlying business quality at entry. Selectivity, not activity, remains the single most reliable driver of returns." },
      { type: "h2", text: "Operational Value Creation, Not Financial Engineering" },
      { type: "p", text: "Multiple expansion is not a plan. The next era of private equity performance will be earned in the operating cadence — pricing discipline, distribution build-out, working capital rigor, and disciplined talent decisions in the first ninety days of ownership." },
      { type: "blockquote", text: "Every dollar of capital deployed is a decision to say no to something else. Great firms internalize that trade-off; the rest simply invest." },
    ],
  },
  {
    slug: "capital-raising-playbook",
    category: "Capital Raising",
    title: "A Founder's Playbook for Institutional Capital Raising",
    dek:
      "What sophisticated investors evaluate before writing a check — and what founders consistently underestimate.",
    date: "2026-01-08",
    readTime: "6 min read",
    image:
      "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "The gap between founders who raise institutional capital and those who cannot is rarely product. It is preparedness. Investor readiness is a discipline — a set of documents, metrics, and decision rights that signal a business is ready to be capitalized." },
      { type: "h2", text: "The Three Documents That Matter" },
      { type: "p", text: "A defensible model, a clean data room, and a concise investor narrative built around unit economics do more to move a process forward than any single introduction." },
      { type: "h2", text: "The Institutional Filter" },
      { type: "p", text: "Institutional investors filter for repeatability, durability, and governance long before they evaluate growth. Founders who understand this rewrite their materials — and shorten their fundraise." },
    ],
  },
  {
    slug: "consumer-brand-investment",
    category: "Consumer Brands",
    title: "Consumer Brand Investing in a Post-DTC Era",
    dek:
      "Brand equity, distribution economics, and the return of the retail shelf as a strategic asset.",
    date: "2025-12-19",
    readTime: "7 min read",
    image:
      "https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "The consumer investment thesis has quietly matured. The direct-to-consumer premium has compressed, and durable brands are once again defined by distribution economics, gross-margin resilience, and their ability to command shelf." },
      { type: "h2", text: "Distribution Is a Balance Sheet Item" },
      { type: "p", text: "Investors are re-underwriting brand equity through the lens of retailer velocity, category authority, and repeat purchase behavior. Distribution is no longer a channel decision — it is a balance-sheet decision." },
    ],
  },
  {
    slug: "manufacturing-growth",
    category: "Manufacturing",
    title: "Manufacturing Growth, Reshoring, and Industrial Capital Formation",
    dek:
      "The industrial base is being rebuilt. The capital stack behind it should be as well.",
    date: "2025-12-02",
    readTime: "9 min read",
    image:
      "https://images.pexels.com/photos/6949931/pexels-photo-6949931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "Reshoring is real, and it is capital-intensive. The businesses that will lead this cycle require patient capital, purpose-built financing, and operating partners who understand throughput as a first-class metric." },
    ],
  },
  {
    slug: "retail-expansion-strategy",
    category: "Retail",
    title: "Retail Expansion Strategy for Growth-Stage Brands",
    dek:
      "Choosing the right doors, the right terms, and the right pace of expansion.",
    date: "2025-11-18",
    readTime: "6 min read",
    image:
      "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "Retail expansion decisions compound. The wrong door, the wrong term, or the wrong pace can compromise unit economics for years. Discipline in door selection is one of the highest-return decisions a growth-stage brand can make." },
    ],
  },
  {
    slug: "family-office-investing",
    category: "Family Office",
    title: "Family Office Investing: Long-Duration Capital for Founder-Led Businesses",
    dek:
      "How family offices are quietly becoming the most patient capital in the middle market.",
    date: "2025-11-04",
    readTime: "5 min read",
    image:
      "https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "Family offices increasingly represent the most patient pool of capital in the middle market. Their duration advantage — measured in decades rather than fund cycles — creates a structural fit with founder-led businesses that require time to compound." },
    ],
  },
  {
    slug: "entrepreneurship-scaling",
    category: "Entrepreneurship",
    title: "From Concept to Investor Readiness: A Framework for Founders",
    dek:
      "The four disciplines every founder must operationalize before scaling capital.",
    date: "2025-10-21",
    readTime: "7 min read",
    image:
      "https://images.pexels.com/photos/6949931/pexels-photo-6949931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "Scaling is not a growth problem — it is a discipline problem. Founders who institutionalize four disciplines (financial cadence, talent architecture, pricing rigor, and governance) unlock capital on materially better terms." },
    ],
  },
  {
    slug: "business-strategy-discipline",
    category: "Business Strategy",
    title: "The Institutional Mindset: Building Enduring Enterprise Value",
    dek:
      "How institutional operating cadences translate into compounding enterprise value.",
    date: "2025-10-07",
    readTime: "6 min read",
    image:
      "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      { type: "p", text: "The institutional mindset is not a template — it is a cadence. Weekly metrics, quarterly capital reviews, and annual strategic rebasing separate businesses that compound from businesses that oscillate." },
    ],
  },
];

export const media = [
  {
    slug: "capital-formation-panel-2026",
    type: "Speaking Engagement",
    outlet: "DFW Capital Formation Forum",
    title: "Panel: The Next Cycle of Middle-Market Capital",
    date: "2026-03-11",
    location: "Dallas, TX",
    image:
      "https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "President Adeshi Moffi joins a panel of institutional investors and operators to discuss capital formation trends in the middle market.",
  },
  {
    slug: "consumer-brand-podcast",
    type: "Podcast",
    outlet: "The Founder Capital Podcast",
    title: "Building Consumer Brands That Deserve Capital",
    date: "2026-01-22",
    location: "Remote",
    image:
      "https://images.pexels.com/photos/6949931/pexels-photo-6949931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "A candid conversation on brand equity, distribution, and the institutional filter for consumer investments.",
  },
  {
    slug: "manufacturing-interview",
    type: "Interview",
    outlet: "Industrial Growth Review",
    title: "Reshoring and the Industrial Capital Stack",
    date: "2025-12-05",
    location: "Print & Digital",
    image:
      "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "An in-depth conversation on the structural capital needs behind the U.S. reshoring cycle.",
  },
  {
    slug: "press-release-firm-expansion",
    type: "Press Release",
    outlet: "Investors Pathway",
    title: "Investors Pathway Expands Advisory Platform for Growth-Stage Brands",
    date: "2025-11-12",
    location: "Allen, TX",
    image:
      "https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "The firm announces an expansion of its advisory platform to serve founder-led consumer, retail, and manufacturing businesses.",
  },
  {
    slug: "retail-strategy-article",
    type: "Article",
    outlet: "Retail Strategy Journal",
    title: "The Door You Choose Is the Balance Sheet You Build",
    date: "2025-10-24",
    location: "Digital",
    image:
      "https://images.pexels.com/photos/6949931/pexels-photo-6949931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "An op-ed on why retail expansion decisions are, at their core, capital allocation decisions.",
  },
  {
    slug: "family-office-news",
    type: "News",
    outlet: "Family Office Capital Weekly",
    title: "Adeshi Moffi on Long-Duration Capital and Founder-Led Businesses",
    date: "2025-09-30",
    location: "Digital",
    image:
      "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt:
      "Coverage of Adeshi Moffi's perspective on family office allocation strategy in the middle market.",
  },
];
